// AssistantWriteNotice — what the assistant just wrote to THIS screen, in words, with
// Undo all (2026-10-13). One component for every screen the chat can write to (Products,
// Catalog), so the batch reads and undoes the same way everywhere. Server side:
// src/assistant-writes.js.
//
// useAssistantWrite({ kind, id, reload, refreshKey }) owns the batch:
//   - reads the open batch for this screen (GET /api/assistant/writes/open), again whenever
//     refreshKey changes: a save made after the batch is what withdraws its Undo;
//   - listens for `inoviu:assistant-turn` (AssistantSection) and then calls reload() and
//     re-reads, so the screen shows the new values without a page reload;
//   - undo() restores the batch, dismiss() keeps the changes and hides the notice.
// <AssistantWriteNotice write groups error onUndo onDismiss /> draws it; groups is
// [{ title, items: [{ name, fields }] }], built by the screen from write.changes.
// Meaning is in words (Alex is colour-blind); the screen may also shade the changed rows.
import { useCallback, useEffect, useState } from 'react';
import Notice from './Notice.jsx';
import { BTN_SECONDARY } from '../theme-tokens.js';

// Column names as the screens show them, not the database's.
const FIELD_LABEL = {
  label: 'Label', status: 'Status', description: 'Description', notes: 'Notes',
  placeable: 'Placeable', qty_param: 'Quantity', labor_line: 'Labour', name: 'Name',
  type: 'Type', typical_value: 'Typical', typical_text: 'Typical', unit: 'Unit',
  size_unit: 'Size Unit', min: 'Min', max: 'Max', configurable: 'Config?',
  value_expr: 'Formula', options: 'Options', price: 'Unit Cost', price_expr: 'Unit Cost',
  hrs: 'Hrs', labor_productivity: 'Productivity', labor_productivity_expr: 'Productivity',
  material: 'Material', size: 'Size',
};
const fieldNames = (fields) => [...new Set(fields.map(f => FIELD_LABEL[f] || f))].join(', ');

export function useAssistantWrite({ kind, id, reload, refreshKey }) {
  const [write, setWrite] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!id) { setWrite(null); return; }
    fetch(`/api/assistant/writes/open?kind=${kind}&id=${id}`)
      .then(r => (r.ok ? r.json() : null))
      .then(setWrite)
      .catch(() => setWrite(null));
  }, [kind, id]);

  useEffect(() => { setError(null); load(); }, [load, refreshKey]);

  useEffect(() => {
    const onTurn = (e) => {
      if (!id || e.detail?.kind !== kind || e.detail?.id !== id) return;
      reload?.();
      load();
    };
    window.addEventListener('inoviu:assistant-turn', onTurn);
    return () => window.removeEventListener('inoviu:assistant-turn', onTurn);
  }, [kind, id, reload, load]);

  const undo = () => {
    setError(null);
    fetch(`/api/assistant/writes/${write.id}/undo`, { method: 'POST' })
      .then(async r => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`); })
      .then(() => { setWrite(null); reload?.(); })
      .catch(err => setError(err.message));
  };
  const dismiss = () => {
    fetch(`/api/assistant/writes/${write.id}/dismiss`, { method: 'POST' }).catch(() => {});
    setWrite(null);
    setError(null);
  };
  return { write, error, undo, dismiss };
}

export default function AssistantWriteNotice({ write, groups = [], error = null, onUndo, onDismiss }) {
  if (!write) return null;
  return (
    <Notice kind="success" onDismiss={onDismiss} title="Dismiss: keep the changes and hide this">
      <strong>Changed by the assistant</strong>
      {write.summary ? `: ${write.summary}` : ''}
      {groups.filter(g => g.items.length).map(g => (
        <div key={g.title}>
          {g.title}: {g.items
            .map(it => (it.fields.includes('new') ? `${it.name} (added)` : `${it.name} (${fieldNames(it.fields)})`))
            .join('; ')}
        </div>
      ))}
      <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {write.undoable ? (
          <button onClick={onUndo} style={BTN_SECONDARY}
                  title="Put back everything as it was before this batch">Undo all</button>
        ) : (
          <span style={{ color: 'var(--th-text-secondary)' }}>
            Saved since, so Undo all is no longer offered. Edit by hand or ask the chat.
          </span>
        )}
        {error && <Notice inline>{error}</Notice>}
      </div>
    </Notice>
  );
}
